import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import Icon from '~/components/atom/Icon';
import { Heading4, Variant } from '~/components/atom/Typography';
import { ClientFlowAction } from '~/graphql/types.client';

import { CONTROLS_LAYER } from '../../../../constants/zIndexes';
import getCardHeading from '../../../../utils/getCardHeading';
import { getCardValues } from '../../../nodeTypes/components/CardTemplate/utils/getCardValues';

type Props = {
  disabled?: boolean;
  actionType: ClientFlowAction['actionType'];
  onClick?: React.MouseEventHandler<HTMLLIElement>;
};

const ListItem: React.FCC<Props> = ({
  disabled = false,
  actionType,
  onClick,
}) => {
  const theme = useTheme();
  const cardValues = getCardValues({
    actionType,
    issueLevel: null,
    theme,
  });

  return (
    <Container
      data-testid={actionType}
      $color={cardValues.color}
      $disabled={disabled}
      onClick={onClick}
    >
      <Icon name={cardValues.icon} margin={[null, 'xxs', null, null]} />
      <Heading4 size="s" margin={[null]} variant={Variant.primary}>
        {getCardHeading(actionType)}
      </Heading4>
    </Container>
  );
};

const Container = styled.li<{
  $disabled?: boolean;
  $color: string;
}>(
  ({ theme, $disabled, $color }) => css`
    cursor: pointer;
    margin-bottom: 8px;

    color: ${$disabled ? theme.color('tertiary', 'dark') : theme.color('text')};
    padding: ${theme.space('base')};

    max-width: 200px;
    width: 100%;
    pointer-events: all;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: ${theme.fs('m')};

    transition: all 0.3s ease-out;

    position: relative;

    > * {
      z-index: ${CONTROLS_LAYER + 1};
    }

    &:before {
      z-index: ${CONTROLS_LAYER + 1};
      content: ' ';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;

      border-left: 4px solid
        ${$disabled ? theme.color('tertiary', 'dark') : $color};
    }

    &:after {
      z-index: 30;
      content: ' ';
      position: absolute;
      top: -${theme.space('xxs')};
      right: -${theme.space('xxs')};
      bottom: -${theme.space('xxs')};
      left: -${theme.space('xxs')};
      background-color: ${theme.color('white')};
      border-radius: ${theme.getTokens().border.radius.base};
      transition: all 0.2s ease-out;
    }

    &:hover {
      z-index: ${CONTROLS_LAYER + 2};
      &:after {
        box-shadow: ${theme.boxShadow('around')};
      }
    }
  `,
);

export default ListItem;
